<template>
  <form @submit.prevent="submitForm" class="login-form">
    <!-- Username field -->
    <div class="form-field">
      <label for="username" class="field-label">Username</label>
      <div class="input-container">
        <div class="input-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </div>
        <input
          id="username"
          type="text"
          v-model="username"
          placeholder="Enter your username"
          class="field-input"
          :class="{ 'input-disabled': loading }"
          :disabled="loading"
          autocomplete="username"
          required
        />
      </div>
    </div>

    <!-- Password field -->
    <div class="form-field">
      <div class="label-row">
        <label for="password" class="field-label">Password</label>
      </div>
      <div class="input-container">
        <div class="input-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
        </div>
        <input
          id="password"
          :type="showPassword ? 'text' : 'password'"
          v-model="password"
          placeholder="Enter your password"
          class="field-input"
          :class="{ 'input-disabled': loading }"
          :disabled="loading"
          autocomplete="current-password"
          required
        />
        <button
          type="button"
          class="visibility-toggle"
          @click="togglePasswordVisibility"
          :disabled="loading"
          tabindex="-1"
        >
          <svg
            v-if="showPassword"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
            ></path>
            <line x1="1" y1="1" x2="23" y2="23"></line>
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        </button>
      </div>
    </div>

    <!-- Submit button -->
    <div class="form-actions">
      <button
        type="submit"
        class="submit-button"
        :disabled="loading || !isFormValid"
      >
        <span v-if="loading" class="button-loader">
          <svg class="spinner" viewBox="0 0 50 50">
            <circle
              class="path"
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke-width="5"
            ></circle>
          </svg>
          <span>Signing in...</span>
        </span>
        <span v-else class="button-text">Sign In</span>
      </button>
    </div>
  </form>
</template>

<script>
import { ref, computed } from "vue";

export default {
  name: "LoginForm",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["submit"],
  setup(props, { emit }) {
    const username = ref("");
    const password = ref("");
    const showPassword = ref(false);

    // Validate form
    const isFormValid = computed(() => {
      return username.value.trim() !== "" && password.value.trim() !== "";
    });

    // Toggle password visibility
    const togglePasswordVisibility = () => {
      showPassword.value = !showPassword.value;
    };

    const submitForm = () => {
      if (!isFormValid.value) return;

      emit("submit", {
        username: username.value,
        password: password.value,
      });
    };

    return {
      username,
      password,
      showPassword,
      isFormValid,
      togglePasswordVisibility,
      submitForm,
    };
  },
};
</script>

<style lang="scss">
// Variables
$primary-color: #3b82f6;
$primary-hover: #2563eb;
$primary-focus: #dbeafe;
$primary-disabled: #93c5fd;

$text-primary: #111827;
$text-secondary: #4b5563;
$text-muted: #6b7280;
$text-light: #9ca3af;
$text-input: #374151;
$text-placeholder: #9ca3af;

$border-color: #e5e7eb;
$border-focus: #93c5fd;
$border-hover: #d1d5db;

$bg-white: #ffffff;
$bg-light: #f9fafb;
$bg-gray: #f3f4f6;
$bg-disabled: #f3f4f6;

$shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
$shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
$shadow-input: 0 1px 2px rgba(0, 0, 0, 0.06);
$shadow-button: 0 1px 3px rgba(0, 0, 0, 0.1);

$radius-sm: 0.25rem;
$radius: 0.375rem;
$radius-md: 0.5rem;
$radius-lg: 0.75rem;
$radius-full: 9999px;

$transition: all 0.2s ease;

// Login form
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

// Form field
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: $text-secondary;
}

// Input container
.input-container {
  position: relative;
  display: flex;
  align-items: center;
}

// Input icon
.input-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: $text-muted;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
}

// Field input
.field-input {
  width: 100%;
  height: 2.75rem;
  padding: 0 2.75rem 0 2.75rem;
  font-size: 0.875rem;
  color: $text-input;
  background-color: $bg-white;
  border: 1px solid $border-color;
  border-radius: $radius;
  box-shadow: $shadow-input;
  transition: $transition;

  &::placeholder {
    color: $text-placeholder;
  }

  &:hover:not(.input-disabled) {
    border-color: $border-hover;
  }

  &:focus {
    outline: none;
    border-color: $border-focus;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.15);
  }

  &.input-disabled {
    background-color: $bg-disabled;
    color: $text-muted;
    cursor: not-allowed;
  }
}

// Password visibility toggle
.visibility-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  color: $text-muted;
  background: none;
  border: none;
  border-radius: $radius;
  cursor: pointer;
  transition: $transition;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover:not(:disabled) {
    color: $text-secondary;
    background-color: $bg-gray;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba($primary-color, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Form actions
.form-actions {
  margin-top: 1rem;
}

// Submit button
.submit-button {
  width: 100%;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: $primary-color;
  border: none;
  border-radius: $radius;
  box-shadow: $shadow-button;
  cursor: pointer;
  transition: $transition;

  &:hover:not(:disabled) {
    background-color: $primary-hover;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.3);
  }

  &:disabled {
    background-color: $primary-disabled;
    cursor: not-allowed;
  }
}

// Button loading state
.button-loader {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .spinner {
    width: 1.25rem;
    height: 1.25rem;
    animation: rotate 2s linear infinite;

    .path {
      stroke: white;
      stroke-linecap: round;
      animation: dash 1.5s ease-in-out infinite;
    }
  }
}

// Animations
@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}
</style>
